/**
 * Desktop game detail V2 — the three-column shell
 * (`design/Games Tonight.dc.html` lines 562-731).
 *
 * 292 rail (303 as shipped — it carries its own scrollbar gutter, see
 * `DetailRail`) · `flex:1` middle capped at 820 · 340 panel. **Each column is its own
 * scroller and the page itself no longer scrolls**, which is the structural
 * change; everything else here follows from it.
 *
 * FULL SCREEN, like the round page: `DesktopApp` drops the top nav for this
 * screen (`headerVM().hdrOn`), and `DetailTopBar` below replaces it with a bar whose
 * only occupant is `◂ <where you came from>`. That bar is the sole way off this
 * screen, which is the whole reason it is pinned to the shell instead of living
 * in the middle column where the back pill used to — an exit indented to the
 * prose's measure is an exit inside the thing it exits.
 *
 * THE HEIGHT CHAIN DOES NOT COME FREE, and a verbatim port of the mirror's
 * `height:100%` silently does nothing. `app/src/styles/global.css` gives `body`
 * no height and `app/index.html` gives `#root` none, so `min-height:0` panes
 * size to their content and none of them ever scrolls. The prototype gets away
 * with it because its runtime owns the wrappers and walks them imperatively
 * (`syncLock()`, :1460-1476). Here the chain is `.qd-detail-lock`, added to
 * `<html>` by `DesktopApp` for this screen only — see `desktop.css`.
 *
 * DESIGN-CHANGELOG's "detail screen owns its own scroll (prototype-only)" entry
 * says there is nothing to port here because "the app's own screen wrapper has
 * real ancestors". That is wrong for this app, and the class above is the proof;
 * the entry is corrected upstream in the same pass that lands this.
 *
 * DOM ORDER IS RAIL → MIDDLE → PANEL, and the mirror's `order:1/2/3` is
 * deliberately NOT ported. Those values exist only because the export emitted
 * the panel before the middle column; keeping them would leave keyboard and
 * screen-reader order running rail → panel → middle, which is neither the
 * reading order nor the visual one.
 *
 * WHAT V2 CUTS FROM THIS SCREEN, and where each thing went:
 *   · the DATA SOURCES chip row — cut outright (`sources` loses its last reader)
 *   · the duplicated PEAK lines — the rail states ALL-TIME PEAK once, and the
 *     live count moved into the chart header where "now" is the caption
 *   · the tag chip row — the rail's TAGS line replaces it
 *   · FRIENDS' INTEREST — the panel's roster replaces it, one row per member
 *   · the bottom "add it to rank it" note — the not-on-list panel replaces it
 *   · the hero, the YOUR CALL strip and the panel pair — absorbed by the three
 *     columns
 *
 * SC-02 is now vacuous on this screen: `remembersScroll('detail')` was already
 * false, and the three panes are descendants `lib/scroll.ts` does not model. It
 * also means `gutter()` returns 0 here, so a modal opened over detail freezes
 * `<body>` while the panes keep scrolling behind it — the module's own issue 3,
 * re-introduced on one screen. Recorded rather than fixed: detail always opens
 * at the top, and per-pane offset memory is new state in a module built around a
 * single document offset.
 */

import { MediaCarousel } from '../../components/MediaCarousel.tsx';
import { useArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { detailVotesRows } from '../../store/selectors.desktop.ts';
import { detailVM, headerVM } from '../../store/selectors.ts';
import { DetailFriendsPanel } from '../components/DetailFriendsPanel.tsx';
import { DetailRail } from '../components/DetailRail.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

/**
 * The middle column's measure. It used to be shared with the back pill that sat
 * above the scroller; the pill is in the top bar now, so the scroller is the
 * only thing wearing it and the measure answers to nothing but the prose.
 */
const MEASURE: CSSProperties = { width: '100%', maxWidth: 820, margin: '0 auto' };

/**
 * The full-screen top bar, geometry copied from the round page's own
 * (`screens/Round.tsx`) so the two screens that hide the nav look like siblings
 * rather than two separate ideas about what replaces it.
 *
 * It carries only the way out. That is the whole point of the bar — with the
 * nav gone, `◂ <where you came from>` is the sole exit, and burying it in the
 * middle column (where it was) puts the exit inside the content it is an exit
 * FROM, indented to the prose's measure rather than pinned to the screen.
 */
function DetailTopBar({ backTxt, onBack }: { backTxt: string; onBack: () => void }): JSX.Element {
  return (
    <div
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '15px 24px',
        borderBottom: '1px solid var(--gt-line)',
        background: 'var(--gt-panel)',
      }}
    >
      <div
        onClick={onBack}
        className="d-hov-acc"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: 'var(--gt-text-4)',
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '.05em',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flex: 'none',
        }}
      >
        ◂ {backTxt}
      </div>
    </div>
  );
}

/** The two group rules that divide the middle column below the description. */
const GROUP: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderTop: '1px solid var(--gt-line)',
};

/** The chart header's two right-hand chips. */
const CHIP: CSSProperties = {
  fontFamily: MONO,
  fontSize: 9.5,
  letterSpacing: '.06em',
  padding: '4px 6px',
};

export default function Detail(): JSX.Element {
  const s = useQd();
  const d = detailVM(s, s.detailId);
  const art = useArt(s.detailId + 'p', d ? d.coverCands : []);

  const backTxt = headerVM(s).backTxt;

  // `headerVM().scr` already falls back to `discover` when the game vanished,
  // so `DesktopApp` never mounts this screen without a game — handled anyway.
  // The bar is drawn even here, and that matters more than it used to: the nav
  // is gone on this screen, so a bodyless render without it would be a dead
  // full-viewport rectangle with no way off it.
  if (!d)
    return (
      <div
        data-screen-label="Game detail"
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <DetailTopBar backTxt={backTxt} onBack={() => s.goBack()} />
      </div>
    );

  // The roster stands off the list too (ownership only) — see detailVotesRows.
  const votesRows = detailVotesRows(s, d.id);

  return (
    <div
      data-screen-label="Game detail"
      style={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <DetailTopBar backTxt={backTxt} onBack={() => s.goBack()} />

      <div
        style={{
          // `relative` stays on the COLUMNS, not on the shell above them: the
          // panel's collapsed tab and its narrow-width drawer both anchor to
          // `top:0`, and against the shell that would now be the top bar's top
          // edge — the tab would hang over the bar and the drawer would cover
          // the way out.
          position: 'relative',
          display: 'flex',
          alignItems: 'stretch',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <DetailRail d={d} art={art} />

        <div
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            // The 20 top matches the rail's, so the two columns start their
            // content on the same line under the bar. It sits OUTSIDE the
            // scroller (as the rail's does) so the prose is clipped at that
            // line rather than sliding up under the bar's border.
            padding: '20px 26px 0',
          }}
        >
          {/* `qd-detail-mid` hides this pane's scrollbar (`desktop.css`). It is the
              one pane whose scroller is the 820 measure rather than the column, so
              its bar drew beside the text instead of on a column edge; the rail and
              the panel keep theirs. Nothing here reserves the 11px it gave back —
              the prose is centred in the measure and a reserved gutter would
              simply push it 11px off centre. */}
          <div
            className="qd-detail-mid"
            style={{
              ...MEASURE,
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
              paddingBottom: 28,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: '#fff' }}>{d.name}</div>
              {/* ONE meta line. The five stacked lines the old hero carried are
                  split between here and the rail's ledger — price, players and the
                  date are facts about the game and belong in the ledger; who made
                  it, where it runs and how it reviews are the pitch. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  fontFamily: MONO,
                  fontSize: 11,
                  letterSpacing: '.04em',
                  color: 'var(--gt-text-5)',
                }}
              >
                <span>{d.dev}</span>
                <span style={{ color: 'var(--gt-faint)' }}>·</span>
                <span>{d.platTxt}</span>
                <span style={{ color: 'var(--gt-faint)' }}>·</span>
                <span style={{ color: d.revClr }}>▮ {d.revTxt}</span>
                {d.hasAdded ? (
                  <>
                    <span style={{ color: 'var(--gt-faint)' }}>·</span>
                    <span style={{ color: 'var(--gt-muted-2)' }}>{d.addedTxt}</span>
                  </>
                ) : null}
              </div>
            </div>

            {/* Bare — V2 drops the MEDIA panel box around the carousel. */}
            <MediaCarousel gameId={d.id} desktop />

            <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--gt-text-3)', maxWidth: 620, textWrap: 'pretty' }}>
              {d.descTxt}
            </div>

            <div style={{ ...GROUP, gap: 16, paddingTop: 16 }}>
              {/* The stats band, unboxed. Its CONTENT is untouched: four of the
                  five figures have no backend job, and a pending cell carries the
                  name of the job it waits on and never a number. The mirror draws
                  all of them filled from seeded sample data — porting its markup
                  would be adopting the seed as a layout. */}
              <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', gap: 24 }}>
                {d.stats.map((c) => (
                  <div
                    key={c.key}
                    style={{
                      flex: '1 1 150px',
                      minWidth: 140,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      textAlign: 'center',
                    }}
                  >
                    <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.11em', color: 'var(--gt-muted)' }}>
                      {c.label}
                    </span>
                    {c.pending ? (
                      <span
                        style={{
                          border: '1px dashed var(--gt-border-3)',
                          padding: '6px 7px',
                          fontFamily: MONO,
                          fontSize: 8.5,
                          lineHeight: 1.6,
                          color: 'var(--gt-muted-2)',
                        }}
                      >
                        {c.why}
                      </span>
                    ) : (
                      <>
                        <span style={{ fontFamily: MONO, fontSize: 14, color: c.color }}>{c.value}</span>
                        <span style={{ fontFamily: MONO, fontSize: 8.5, color: 'var(--gt-muted-2)' }}>{c.caption}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ ...GROUP, gap: 10, paddingTop: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.11em', color: 'var(--gt-muted)' }}>
                    PLAYERS ONLINE · 90D
                  </span>
                  <div style={{ flex: 1 }} />
                  {/* The live count, which V2's cut of the PEAK line would
                      otherwise have removed from the app entirely. Same freshness
                      gate it always had; the caption around it now says "now"
                      rather than "peak", which is what makes it honest here. */}
                  {d.nowTxt ? (
                    <span style={{ ...CHIP, color: 'var(--gt-text-3)', border: '1px solid var(--gt-border-2)' }}>
                      {d.nowTxt}
                    </span>
                  ) : null}
                  {/* Non-optional on landing day: real accumulation started
                      2026-08-11, so this chart is a few bars wide and saying so is
                      the difference between a short series and a dead game. */}
                  {d.trackedTxt ? (
                    <span style={{ ...CHIP, color: 'var(--gt-muted-2)', border: '1px dashed var(--gt-border-2)' }}>
                      {d.trackedTxt}
                    </span>
                  ) : null}
                  {/* The mirror ships this chip unconditionally, from when the
                      chart was always a PRNG. It is gated now because the chart
                      reads measured samples — only demo mode fabricates a series. */}
                  {d.chartIsSample ? (
                    <span style={{ ...CHIP, color: 'var(--gt-muted-2)', border: '1px dashed var(--gt-border-2)' }}>
                      SAMPLE DATA
                    </span>
                  ) : null}
                </div>
                {d.hasChart ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 88 }}>
                      {d.bars.map((b) => (
                        <div key={b.key} className="d-hov-bright" data-tip={b.tip} style={{ flex: 1, height: b.h, background: b.c }} />
                      ))}
                    </div>
                    {/* Axis ends only — the old footer's middle slot carried a
                        90-day-window PEAK that duplicated the rail's all-time one.
                        The window max survives in the per-bar tooltips. */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 14,
                        fontFamily: MONO,
                        fontSize: 9,
                        letterSpacing: '.05em',
                        color: 'var(--gt-muted-2)',
                      }}
                    >
                      <span>90D AGO</span>
                      <div style={{ flex: 1, height: 1, background: 'var(--gt-line)' }} />
                      <span>TODAY</span>
                    </div>
                  </>
                ) : null}
                {d.noChart ? (
                  <div
                    style={{
                      height: 88,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed var(--gt-border)',
                      fontFamily: MONO,
                      fontSize: 10.5,
                      color: 'var(--gt-muted-2)',
                      textAlign: 'center',
                      padding: '0 20px',
                      lineHeight: 1.8,
                    }}
                  >
                    NO PLAYER DATA YET — {d.noChartWhy}
                  </div>
                ) : null}

                {/* PRICE · 12 MONTHS (migration 3045). A sibling of the chart
                    above rather than a band cell: a sparkline plus a chip does
                    not fit a StatCell's value/caption pair, and the design draws
                    it as its own section too. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingTop: 6 }}>
                  <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.11em', color: 'var(--gt-muted)' }}>
                    PRICE · 12 MONTHS
                  </span>
                  <div style={{ flex: 1 }} />
                  {/* The accent goes on the LOW chip because "it has been
                      cheaper, and here is when" is what a reader opens this for.
                      Withheld entirely until the price has actually moved below
                      its ceiling — on night one every low IS the current price,
                      and a superlative over one sample is not a history. */}
                  {d.hasPriceLow ? (
                    <span style={{ ...CHIP, color: 'var(--acc)', border: '1px solid var(--acc)' }}>
                      LOW {d.priceLowTxt} · {d.priceLowWhen}
                    </span>
                  ) : null}
                  {d.priceIsSample ? (
                    <span style={{ ...CHIP, color: 'var(--gt-muted-2)', border: '1px dashed var(--gt-border-2)' }}>
                      SAMPLE DATA
                    </span>
                  ) : null}
                </div>
                {d.hasPriceChart ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 56 }}>
                      {d.priceBars.map((b) => (
                        <div key={b.key} className="d-hov-bright" data-tip={b.tip} style={{ flex: 1, height: b.h, background: b.c }} />
                      ))}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 14,
                        fontFamily: MONO,
                        fontSize: 9,
                        letterSpacing: '.05em',
                        color: 'var(--gt-muted-2)',
                      }}
                    >
                      <span>12M AGO</span>
                      <div style={{ flex: 1, height: 1, background: 'var(--gt-line)' }} />
                      <span>{d.priceFoot}</span>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed var(--gt-border)',
                      fontFamily: MONO,
                      fontSize: 10.5,
                      color: 'var(--gt-muted-2)',
                      textAlign: 'center',
                      padding: '0 20px',
                      lineHeight: 1.8,
                    }}
                  >
                    {d.noPriceChartWhy}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DetailFriendsPanel d={d} rows={votesRows} />
      </div>
    </div>
  );
}
